import React, { useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { LinkButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { InfoLabel } from '@eika/label';
import { Modal } from '@eika/modal';
import { ButtonToggles } from '@eika/radio-button';

import { UpdateConsent } from '../../actions';
import { StringKeyValue } from '../../actions/types';
import { getConsentText } from '../../config/consentTexts';
import { ConsentData } from '../../reducers/consent';

interface Props {
    updateConsent: (name: UpdateConsent['name'], value: UpdateConsent['value']) => void;
    text: StringKeyValue;
    data: ConsentData;
}

const Consent = ({ data, updateConsent, text }: Props): JSX.Element => {
    const id: string = useRef(uuid()).current;
    const [showModal, setShowModal] = useState(false);

    const { infoTekst, svarNeiTekst, samtykkeId, svar, tittel } = data;

    // Texts are indexed locally on `samtykkeId.value` since the back-end
    // response only provides `tittel` and an (often empty) `tekster` array.
    const consentText = getConsentText(samtykkeId.value);
    const resolvedConsentTitle = consentText?.modalTitle || tittel;
    const resolvedConsentLabel = consentText?.label || tittel || infoTekst;
    const modalParagraphs = consentText?.modalText ?? [];
    const hasModal = modalParagraphs.length > 0;

    const consentOptions = [
        { label: 'Ja', value: 'JA' },
        { label: 'Nei', value: 'NEI' },
    ];

    return (
        <div className="consent-item" id={id}>
            <ButtonToggles
                className="consent-item__options"
                label={resolvedConsentLabel}
                options={consentOptions}
                selectedOption={svar}
                onChange={(value) => updateConsent(samtykkeId.value, value)}
            />

            {hasModal && (
                <>
                    <LinkButton
                        className="consent-item__open-modal"
                        icon={IconTypes.INFO_24}
                        onClick={() => setShowModal(!showModal)}
                    >
                        {text.consentModalButtonOpenText}
                    </LinkButton>

                    <Modal
                        id={`modal_${id}`}
                        title={resolvedConsentTitle}
                        onClose={() => setShowModal(false)}
                        setOpen={(open) => setShowModal(open)}
                        open={showModal}
                        appNamespace="no-eika-lokalbank"
                    >
                        {modalParagraphs.map((paragraph) => (
                            <p key={uuid()}>{paragraph}</p>
                        ))}

                        <div className="consent-item__close-modal">
                            <StandardButton onClick={() => setShowModal(false)}>
                                {text.consentModalButtonCloseText}
                            </StandardButton>
                        </div>
                    </Modal>
                </>
            )}
            {svar === 'NEI' && svarNeiTekst && <InfoLabel className="consent-item__info" text={svarNeiTekst} />}
        </div>
    );
};

export default Consent;
